import { Container } from "../widgets";

import { useContext, useRef, useState } from "react";
import { EditorContext } from "../context/EditorContext";
import { getDropZone, getInsertIndex } from "../utils/dragAndDrop";
import PersonaScopeGuard from "../widgets/_shared/PersonaScopeGuard";

/** @typedef {import("../widgets").BaseWidget} BaseWidget */

/**
 * The widget elements directly inside a container element, without the one
 * being dragged — in render order, which is the order rebuildOrder counts in.
 *
 * @param {Element|null} containerEl
 * @returns {Element[]}
 */
export const siblingWidgetEls = (containerEl) => Array.from(containerEl?.children || [])
    .filter(el => el.classList.contains('report-builder-widget'))
    .filter(el => !el.classList.contains('dragging'));

/**
 * Renders a widget as actual rendered 'view' on the canvas
 * Recursively renders any child widgets
 * 
 * @param {object} props
 * @param {BaseWidget} props.widget The widget instance to render
 * @param {BaseWidget[]} props.widgets List of all widgets (to find children)
 * @param {boolean} [props.editable=true] Whether the widget is editable 
 * @param {boolean} [props.debug=false] Displays debug information
 * @param {number} [props.depth=0] The current widget depth in the tree
 * @returns 
 */
export const CanvasNode = ({
    widget,
    widgets,
    depth = 0,
    editable = true,
    debug = false,
}) => {

    /** Editor functionality is only available when the canvas is in edit mode. */
    const editorCtx = useContext(EditorContext);

    /**
     * Whether *this* widget is being dragged
     */
    const [dragging, setDragging] = useState(false);

    /**
     * Which edge of a *target* widget is being dragged over. 
     * @typedef {'top' | 'right' | 'bottom' | 'left' | 'inside'} DropTargetValue
     */
    const [dropTarget, setDropTarget] = useState(/** @type {DropTargetValue|null} */ (null));

    /**
     * Where an inside-drop on a container lands among its children, and where
     * the line that shows it goes (px from the container's top or left edge).
     * Null for an empty container: that one keeps the dashed outline.
     * @typedef {object} InsertPosition
     * @property {number} index - Index among the children (excluding the dragged widget)
     * @property {number} offset - Line position relative to this node
     * @property {boolean} isRow - Whether the children run left-to-right
     */
    const [insertPosition, setInsertPosition] = useState(/** @type {InsertPosition|null} */ (null));

    /** @type {React.RefObject<HTMLDivElement>} */
    const ref = useRef(null);

    /* Find all children from the widgets array */
    if (widget instanceof Container) {
        
        const children = widgets
            .filter(w => w.parent === widget.id)
            .filter(w => !w.deleted); // Filter out deleted children

        const childNodes = children.map(child => (
            <CanvasNode
                key={child.id}
                widget={child}
                widgets={widgets}
                editable={editable}
                depth={depth + 1}
            />
        ));

        widget.childNodes = childNodes;
    }

    widget.editable = editable;

    /* Whether the widget is clicked/active */
    const isActive = editorCtx && (widget.id === editorCtx.activeWidgetId);
    const isHovered = editorCtx && (widget.id === editorCtx.hoveredWidgetId);

    /* CSS class list */
    const classNames = [
        'report-builder-widget',
        widget.constructor.name,
        editable && isActive && 'selected',
        editable && isHovered && 'hovered',
        editable && 'editable',
        dragging && 'dragging',
    ].filter(Boolean).join(' ');

    if (widget.deleted) {
        return null;
    }

    const isDraggable = editable && widget.parent !== null; // Root container is not draggable

    /**
     * Measures where an inside-drop lands, from the pointer and the rendered
     * children. Without this every inside-drop went to index 0 (the top: the
     * old code read `widget.children.length`, which is never filled) — also in
     * the gap between two blocks — and the whole container lit up, so you
     * couldn't see where the block would land.
     * Updateverzoek Team Sportclub, 2026-10-14.
     *
     * @param {React.DragEvent<HTMLDivElement>} e
     * @returns {InsertPosition|null}
     */
    const measureInsertPosition = (e) => {
        const node = ref.current;
        if (!node) return null;

        // Direct child widgets only, and not the one being dragged:
        // rebuildOrder counts the index among the siblings without it.
        const childEls = siblingWidgetEls(node.querySelector(':scope > .report-builder-container'));
        if (childEls.length === 0) return null;

        const isRow = Boolean(widget.getSetting('flex')?.flexDirection?.startsWith('row'));
        const rects = childEls.map(el => el.getBoundingClientRect());
        const index = getInsertIndex({
            pointer: isRow ? e.clientX : e.clientY,
            childRects: rects,
            axis: isRow ? 'x' : 'y',
        });

        const start = (r) => (isRow ? r.left : r.top);
        const end = (r) => (isRow ? r.right : r.bottom);
        // In the middle of the gap between two children; before the first or
        // after the last child just outside it.
        const position = index === 0
            ? start(rects[0]) - 4
            : index === rects.length
                ? end(rects[rects.length - 1])
                : (end(rects[index - 1]) + start(rects[index])) / 2 - 2;
        const nodeRect = node.getBoundingClientRect();

        return { index, offset: Math.round(position - (isRow ? nodeRect.left : nodeRect.top)), isRow };
    };

    /**
     * Stores a measured insert position, but keeps the previous object when
     * nothing changed. dragover fires ~20×/s even with a still mouse; a new
     * object each time re-rendered this whole subtree (the full report, over
     * the root) on every event.
     *
     * @param {InsertPosition|null} next
     */
    const updateInsertPosition = (next) => {
        setInsertPosition(prev => (
            prev && next
            && prev.index === next.index
            && prev.offset === next.offset
            && prev.isRow === next.isRow
        ) ? prev : next);
    };

    /** Clears both pieces of drop feedback. */
    const clearDropFeedback = () => {
        setDropTarget(null);
        setInsertPosition(null);
    };

    const dragOver = (dropTarget === 'inside' && (widget instanceof Container) && !insertPosition); 

    const dragStyles = dragOver ? {
        backgroundColor: '#f0f8ff',
        outline: '2px dashed #1e90ff',
    } : {};

    return (
        <div
            ref={ref}
            className={classNames}
            data-id={widget.id}
            data-drop-target={dropTarget}
            style={{ 
                // @ts-ignore 
                position: 'relative', 
                ...widget.getStyles?.(),
                ...dragStyles
            }}
            draggable={isDraggable}
            onDragStart={(e) => {
                if (!editable) return;

                e.stopPropagation(); // Prevent parent widgets from also handling drag
                e.dataTransfer.setData('id', widget.id);
                e.dataTransfer.setData('type', widget.constructor.name);
                setDragging(true);
            }}
            onDragEnd={(e) => {
                if (!editable) return;
                e.stopPropagation();
                e.preventDefault();
                setDragging(false);
            }}
            onDragOver={(e) => {
                if (!editable) return;
                if (!editorCtx) return;

                e.stopPropagation();
                e.preventDefault();

                // Root container has no parent and no siblings — only inside-drops apply.
                if (!widget.parent) {
                    if (widget instanceof Container) {
                        setDropTarget('inside');
                        updateInsertPosition(measureInsertPosition(e));
                    }
                    return;
                }

                const parentFlex = editorCtx
                    .getWidgetById(widget.parent)
                    ?.getSetting('flex');

                // Calculate based on the bounding client rect of the dragged target and the mouse position whether the drop edge is top/bottom (for column flex) or left/right (for row flex)
                const targetRect = e.currentTarget.getBoundingClientRect();
                const isRow = parentFlex?.flexDirection?.startsWith('row');
                const mousePos = isRow ? e.clientX : e.clientY;

                const zone = getDropZone({
                    pointer: mousePos,
                    rect: targetRect,
                    axis: isRow ? 'x' : 'y',
                });

                /** @type {typeof dropTarget} */
                let newdropTarget = null;

                if (zone === 'before') {
                    newdropTarget = isRow ? 'left' : 'top';
                } else if (zone === 'after') {
                    newdropTarget = isRow ? 'right' : 'bottom';
                } else if (widget instanceof Container) {
                    newdropTarget = 'inside';
                }

                setDropTarget(newdropTarget);
                updateInsertPosition(newdropTarget === 'inside' ? measureInsertPosition(e) : null);
            }}
            onDragLeave={(e) => {
                if (!editable) return;

                e.stopPropagation();
                e.preventDefault();

                clearDropFeedback();
            }}
            onDrop={e => {
                if (!editable) return;
                if (!editorCtx) return;

                e.stopPropagation();
                e.preventDefault();

                const droppedId = e.dataTransfer.getData('id');
                const type = e.dataTransfer.getData('type');

                const droppedWidget = editorCtx.getWidgetById(droppedId);

                // Read the feedback state, then clear it before any early
                // return: dropping on yourself left the line standing.
                const dropEdge = dropTarget;
                const insertAt = insertPosition;
                clearDropFeedback();

                if (!widget) {
                    return; // A valid drop target is required
                }

                if (widget.id === droppedWidget?.id) {
                    return; // Can't drop on self
                }

                if (!dropEdge) {
                    return; // A valid drop edge/center is required
                }

                const _el = /** @type {HTMLElement} */ (ref.current);
                const htmlParent = /** @type {HTMLElement} */ (_el.parentNode);
                // Among the sibling widgets without the dragged one, the same
                // way rebuildOrder counts: counting it put an edge-drop one
                // place too far when moving a block down within its container.
                const indexOfTarget = siblingWidgetEls(htmlParent).indexOf(_el);

                let targetIndex = indexOfTarget; // @todo +- 1 based on edge

                if (dropEdge === 'inside') {
                    if (widget instanceof Container) {
                        // Where the line was shown; an empty container (no line) has one place.
                        targetIndex = insertAt ? insertAt.index : 0;
                    } else {
                        targetIndex = indexOfTarget; // Drop on non-container -> becomes sibling
                    }
                } else if (dropEdge === 'bottom' || dropEdge === 'right') {
                    targetIndex = indexOfTarget + 1; // Drop *after* the target element
                }

                /* Set target parent ID based on drop edge/center */
                const parentId = (widget instanceof Container)
                    ? (dropEdge === 'inside')
                        ? widget.id       // Inside-drop on a container -> use that container. 
                        : widget.parent   // Edge-drop on container -> should become sibling. 
                    : widget.parent;      // Not a container -> should become sibling. 

                if (droppedId === widget.id) return;

                editorCtx.handleWidgetDrop({
                    droppedId,
                    type,
                    parentId,
                    targetIndex,
                });
            }}
            onClick={(e) => {
                if (!editable || !editorCtx) {
                    return;
                }
                
                e.stopPropagation(); // Prevent parent widgets from also handling click

                if (widget instanceof Container) {
                    if (widget.children.length === 0) {
                        return; // Let the container handle clicks on empty space
                    }
                }

                /* Select the widget */
                editorCtx.setActiveWidgetId(widget.id);
            }}
        >
            {/* Drag top/left/right/bottom edge indicator, the 'center' value is done by background */}
            {/* Inside-drop on a container with children: a line at the spot
                the block will land, instead of outlining the whole container.
                Only top/left inline — it changes per mouse move (ADR-054). */}
            {editable && dropTarget === 'inside' && insertPosition && <div
                className={`canvas-insert-line ${insertPosition.isRow ? 'canvas-insert-line--row' : ''}`}
                style={insertPosition.isRow ? { left: insertPosition.offset } : { top: insertPosition.offset }}
            />}

            {editable && dropTarget && !(dropTarget === 'inside' && insertPosition) && <div style={{
                position: 'absolute',
                zIndex: 50,
                top: dropTarget === 'top' ? 0 : undefined,
                left: dropTarget === 'left' ? 0 : undefined,
                right: dropTarget === 'right' ? 0 : undefined,
                bottom: dropTarget === 'bottom' ? 0 : undefined,
                width: (dropTarget === 'left' || dropTarget === 'right') ? '4px' : '100%',
                height: (dropTarget === 'top' || dropTarget === 'bottom') ? '4px' : '100%',
                backgroundColor: '#1e90ff',
                opacity: 0.75,
                pointerEvents: 'none',
            }} />}

            <PersonaScopeGuard widget={widget}>
                {widget.render(widget.settings)}
            </PersonaScopeGuard>

            {debug && <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '10px',
                height: '10px',
                padding: '2px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                border: '2px solid blue',
                borderRight: 'none',
                borderBottom: 'none',
                boxSizing: 'border-box',
                fontFamily: 'monospace',
                fontSize: '10px',
                color: 'blue',
                opacity: 0.65,
            }}>
                {/* {widget.id} */}
                {widget.id} {dropTarget}
            </div>}
        </div>
    );
}
